import type { Direction } from '@/types/game'
import type { Tile } from './tiles'
import { isSolid, isHazard } from './tiles'

export interface Vec {
  x: number
  y: number
}

export type StopReason =
  | 'wall'
  | 'edge'
  | 'ice-exit'
  | 'hazard'
  | 'home'
  | 'breakable'
  | 'teleport'
  | 'locked'
  | 'blocked'

export type SlideEvent =
  | { kind: 'coin'; x: number; y: number }
  | { kind: 'gem'; x: number; y: number }
  | { kind: 'key'; x: number; y: number }
  | { kind: 'unlock'; x: number; y: number }
  | { kind: 'switch'; x: number; y: number }
  | { kind: 'teleport'; from: Vec; to: Vec }
  | { kind: 'shatter'; x: number; y: number }
  | { kind: 'hazard'; x: number; y: number }
  | { kind: 'home'; x: number; y: number }
  | { kind: 'impact'; x: number; y: number }
  | { kind: 'ice'; x: number; y: number }

export interface SlideResult {
  path: Vec[]
  end: Vec
  stopReason: StopReason
  events: SlideEvent[]
  died: boolean
  reachedHome: boolean
}

export interface GridState {
  rows: number
  cols: number
  cells: Tile[][]
  keys: number
  switches: Record<string, boolean>
  teleports: Vec[]
}

const DIRS: Record<Direction, Vec> = {
  up: { x: 0, y: -1 },
  down: { x: 0, y: 1 },
  left: { x: -1, y: 0 },
  right: { x: 1, y: 0 },
}

export function tileAt(state: GridState, x: number, y: number): Tile | null {
  if (y < 0 || y >= state.rows || x < 0 || x >= state.cols) return null
  const row = state.cells[y]
  if (x >= row.length) return null
  return row[x]
}

function cloneCells(cells: Tile[][]): Tile[][] {
  return cells.map((r) => r.slice())
}

export function cloneState(state: GridState): GridState {
  return {
    rows: state.rows,
    cols: state.cols,
    cells: cloneCells(state.cells),
    keys: state.keys,
    switches: { ...state.switches },
    teleports: state.teleports.map((t) => ({ ...t })),
  }
}

function partnerTeleport(state: GridState, x: number, y: number): Vec | null {
  const others = state.teleports.filter((t) => !(t.x === x && t.y === y))
  if (others.length === 0) return null
  return others[0]
}

export function resolveSlide(state: GridState, from: Vec, dir: Direction): SlideResult {
  const d = DIRS[dir]
  const path: Vec[] = [{ ...from }]
  const events: SlideEvent[] = []
  let pos: Vec = { ...from }
  let guard = 0
  const maxSteps = (state.rows + state.cols) * 4 + 8
  let wasOnIce = tileAt(state, from.x, from.y) === 's'

  while (guard++ < maxSteps) {
    const nx = pos.x + d.x
    const ny = pos.y + d.y
    const next = tileAt(state, nx, ny)

    if (next === null) {
      return finish(path, pos, 'edge', events)
    }

    if (next === 'x') {
      return finish(path, pos, 'wall', events)
    }

    if (next === 'd') {
      if (state.keys > 0) {
        events.push({ kind: 'unlock', x: nx, y: ny })
        state.keys -= 1
        state.cells[ny][nx] = '-'
        pos = { x: nx, y: ny }
        path.push({ ...pos })
        continue
      }
      return finish(path, pos, 'locked', events)
    }

    if (next === 'b') {
      events.push({ kind: 'shatter', x: nx, y: ny })
      state.cells[ny][nx] = '-'
      return finish(path, pos, 'breakable', events)
    }

    pos = { x: nx, y: ny }
    path.push({ ...pos })

    const here = state.cells[pos.y][pos.x]

    if (here === 'p') {
      events.push({ kind: 'coin', x: pos.x, y: pos.y })
      state.cells[pos.y][pos.x] = '-'
    } else if (here === 'g') {
      events.push({ kind: 'gem', x: pos.x, y: pos.y })
      state.cells[pos.y][pos.x] = '-'
    } else if (here === 'k') {
      events.push({ kind: 'key', x: pos.x, y: pos.y })
      state.keys += 1
      state.cells[pos.y][pos.x] = '-'
    } else if (here === 'w') {
      events.push({ kind: 'switch', x: pos.x, y: pos.y })
      toggleSwitch(state)
    }

    if (here === 'e') {
      events.push({ kind: 'home', x: pos.x, y: pos.y })
      const r = finish(path, pos, 'home', events)
      r.reachedHome = true
      return r
    }

    if (isHazard(here)) {
      events.push({ kind: 'hazard', x: pos.x, y: pos.y })
      const r = finish(path, pos, 'hazard', events)
      r.died = true
      return r
    }

    if (here === 't') {
      const partner = partnerTeleport(state, pos.x, pos.y)
      if (partner) {
        events.push({ kind: 'teleport', from: { ...pos }, to: { ...partner } })
        pos = { x: partner.x, y: partner.y }
        path.push({ ...pos })
        const landed = state.cells[pos.y][pos.x]
        if (landed === 'e') {
          events.push({ kind: 'home', x: pos.x, y: pos.y })
          const r = finish(path, pos, 'home', events)
          r.reachedHome = true
          return r
        }
        if (isHazard(landed)) {
          events.push({ kind: 'hazard', x: pos.x, y: pos.y })
          const r = finish(path, pos, 'hazard', events)
          r.died = true
          return r
        }
        return finish(path, pos, 'teleport', events)
      }
    }

    if (wasOnIce && here !== 's') {
      return finish(path, pos, 'ice-exit', events)
    }

    if (here === 's') {
      events.push({ kind: 'ice', x: pos.x, y: pos.y })
      wasOnIce = true
      continue
    }

    wasOnIce = false

    continue
  }

  return finish(path, pos, 'blocked', events)
}

function toggleSwitch(state: GridState) {
  for (let y = 0; y < state.rows; y++) {
    for (let x = 0; x < state.cells[y].length; x++) {
      const t = state.cells[y][x]
      if (t === 'd') state.cells[y][x] = '-'
    }
  }
}

function finish(path: Vec[], pos: Vec, reason: StopReason, events: SlideEvent[]): SlideResult {
  if (reason === 'wall' || reason === 'edge' || reason === 'breakable' || reason === 'locked') {
    events.push({ kind: 'impact', x: pos.x, y: pos.y })
  }
  return {
    path,
    end: { ...pos },
    stopReason: reason,
    events,
    died: false,
    reachedHome: false,
  }
}

export function findTile(cells: Tile[][], target: Tile): Vec | null {
  for (let y = 0; y < cells.length; y++) {
    for (let x = 0; x < cells[y].length; x++) {
      if (cells[y][x] === target) return { x, y }
    }
  }
  return null
}

export function collectTiles(cells: Tile[][], target: Tile): Vec[] {
  const out: Vec[] = []
  for (let y = 0; y < cells.length; y++) {
    for (let x = 0; x < cells[y].length; x++) {
      if (cells[y][x] === target) out.push({ x, y })
    }
  }
  return out
}

export function buildGridState(grid: string[]): GridState {
  const rows = grid.length
  const cols = Math.max(...grid.map((r) => r.length))
  const cells: Tile[][] = grid.map((r) => {
    const arr = r.split('') as Tile[]
    while (arr.length < cols) arr.push('-')
    return arr
  })
  const teleports = collectTiles(cells, 't')
  return { rows, cols, cells, keys: 0, switches: {}, teleports }
}

export const _internal = { DIRS, isSolid }
